import { useMemo } from 'react';
import { PiCaretLeftBold, PiCaretRightBold } from 'react-icons/pi';
import { cn } from '@v2/utils/cn';
import type { CalendarEvent } from './types';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const colorMap: Record<string, string> = {
    sky:     'bg-sky-100 border-sky-300 text-sky-900 hover:bg-sky-200 dark:bg-sky-900/40 dark:border-sky-700 dark:text-sky-200 dark:hover:bg-sky-900/60',
    emerald: 'bg-emerald-100 border-emerald-400 text-emerald-900 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:border-emerald-700 dark:text-emerald-200 dark:hover:bg-emerald-900/60',
    rose:    'bg-rose-100 border-rose-400 text-rose-900 hover:bg-rose-200 dark:bg-rose-900/40 dark:border-rose-700 dark:text-rose-200 dark:hover:bg-rose-900/60',
    amber:   'bg-amber-100 border-amber-400 text-amber-950 hover:bg-amber-200 dark:bg-amber-900/40 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-900/60',
    violet:  'bg-violet-100 border-violet-400 text-violet-900 hover:bg-violet-200 dark:bg-violet-900/40 dark:border-violet-700 dark:text-violet-200 dark:hover:bg-violet-900/60',
};

type Props = {
    month: Date;
    events: CalendarEvent[];
    onMonthChange: (d: Date) => void;
    onEventClick: (event: CalendarEvent) => void;
    selectedEventId?: string | null;
};

function pad(n: number) { return String(n).padStart(2, '0'); }
function toYmd(d: Date)  { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

export default function EventMonthCalendar({ month, events, onMonthChange, onEventClick, selectedEventId }: Props) {
    const { cells, monthLabel } = useMemo(() => {
        const y = month.getFullYear();
        const m = month.getMonth();
        const first = new Date(y, m, 1);
        const startPad = first.getDay();
        const daysInMonth = new Date(y, m + 1, 0).getDate();
        const total = Math.ceil((startPad + daysInMonth) / 7) * 7;

        const byDate = events.reduce<Record<string, CalendarEvent[]>>((acc, ev) => {
            (acc[ev.date] ??= []).push(ev);
            return acc;
        }, {});

        const cells = [];
        for (let i = 0; i < total; i++) {
            const dayNum = i - startPad + 1;
            const inMonth = dayNum >= 1 && dayNum <= daysInMonth;
            const date = inMonth ? new Date(y, m, dayNum) : null;
            const key = date ? toYmd(date) : `pad-${i}`;
            cells.push({
                key,
                inMonth,
                dayNum: inMonth ? dayNum : null,
                dateKey: date ? toYmd(date) : null,
                events: date ? (byDate[toYmd(date)] ?? []) : [],
                isToday: date ? toYmd(date) === toYmd(new Date()) : false,
            });
        }

        const monthLabel = first.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
        return { cells, monthLabel };
    }, [month, events]);

    const prev = () => onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1));
    const next = () => onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1));

    return (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
            {/* Header do mês */}
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-700/60">
                <button
                    type="button"
                    onClick={prev}
                    className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
                    aria-label="Mês anterior"
                >
                    <PiCaretLeftBold className="size-4" />
                </button>
                <h2 className="text-base font-semibold capitalize text-gray-900 dark:text-gray-100">{monthLabel}</h2>
                <button
                    type="button"
                    onClick={next}
                    className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
                    aria-label="Próximo mês"
                >
                    <PiCaretRightBold className="size-4" />
                </button>
            </div>

            {/* Dias da semana */}
            <div className="grid grid-cols-7 border-b border-gray-100 bg-gray-50/80 text-center text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-700/50 dark:bg-gray-800/40 dark:text-gray-400">
                {WEEKDAYS.map((d) => (
                    <div key={d} className="py-2">{d}</div>
                ))}
            </div>

            {/* Grade de dias */}
            <div className="grid grid-cols-7">
                {cells.map((cell) => (
                    <div
                        key={cell.key}
                        className={cn(
                            'min-h-[7.5rem] border-b border-r border-gray-100 p-1.5 last:border-r-0 dark:border-gray-700/40',
                            !cell.inMonth && 'bg-gray-50/60 dark:bg-gray-800/30',
                            cell.isToday && 'bg-amber-50/40 dark:bg-amber-950/20',
                        )}
                    >
                        {cell.dayNum != null && (
                            <span
                                className={cn(
                                    'mb-1 inline-flex size-6 items-center justify-center rounded-full text-xs font-semibold',
                                    cell.isToday ? 'bg-amber-500 text-white' : 'text-gray-700 dark:text-gray-300',
                                )}
                            >
                                {cell.dayNum}
                            </span>
                        )}
                        <div className="flex flex-col gap-0.5">
                            {cell.events.slice(0, 4).map((ev) => (
                                <button
                                    key={ev.id}
                                    type="button"
                                    onClick={() => onEventClick(ev)}
                                    className={cn(
                                        'w-full truncate rounded border px-1 py-0.5 text-left text-[10px] font-semibold leading-tight transition',
                                        colorMap[ev.color] ?? colorMap.sky,
                                        selectedEventId === ev.id && 'ring-2 ring-gray-900 ring-offset-1 dark:ring-gray-100',
                                    )}
                                    title={`${ev.title} — ${ev.subtitle}`}
                                >
                                    <span className="block truncate">{ev.title}</span>
                                    <span className="block truncate text-[9px] font-normal opacity-80">
                                        {ev.event_type === 'etd' ? 'ETD' : 'ETA'} · {ev.order_number}
                                    </span>
                                </button>
                            ))}
                            {cell.events.length > 4 && (
                                <span className="text-[9px] text-gray-500 dark:text-gray-500">+{cell.events.length - 4} mais</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
